import { useId, useState } from 'react'

import { Label } from '@/components/ui/label'
import { PillRadio } from '@/components/ui/pill-radio'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/utils/tw-helpers'

import { QuestionProps, TChoiceStyle } from './types'

interface SingleChoiceQuestionProps extends QuestionProps {
  defaultValue?: string
  labelClassName?: string
  choiceStyle?: TChoiceStyle
}

export const SingleChoiceQuestion = ({
  question,
  defaultValue,
  labelClassName,
  choiceStyle = 'normal',
  onValueChange,
}: SingleChoiceQuestionProps): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState<string>(defaultValue || '')
  const id = useId()

  const handleOptionSelect = (option: string) => {
    setSelectedOption(option)
    onValueChange?.({
      value: option,
    })
  }

  const isPillStyle = choiceStyle === 'pill'

  return (
    <section>
      <h4 className={cn('mb-4 text-[13px] font-bold text-secondary-20', labelClassName)}>
        {question.question}
        {question.required && <span className="text-danger"> ※</span>}
      </h4>
      {isPillStyle ? (
        <div role="radiogroup" aria-label={question.question} className="flex flex-wrap gap-2.5">
          {question.surveyQuestionChoices?.map((option, index) => (
            <PillRadio
              key={option.id}
              id={`${id}-option-${index}`}
              name={`${id}-radio-group`}
              value={option.id}
              checked={selectedOption === option.id}
              onChange={() => handleOptionSelect(option.id)}
            >
              {option.choiceText}
            </PillRadio>
          ))}
        </div>
      ) : (
        <RadioGroup
          aria-label={question.question}
          className="flex flex-col gap-3"
          value={selectedOption}
          onValueChange={handleOptionSelect}
        >
          {question.surveyQuestionChoices?.map((option, index) => (
            <div key={option.id} className="flex items-center gap-2">
              <RadioGroupItem id={`${id}-option-${index}`} value={option.id} />
              <Label
                htmlFor={`${id}-option-${index}`}
                className="text-sm font-medium text-primary-text"
              >
                {option.choiceText}
              </Label>
            </div>
          ))}
        </RadioGroup>
      )}
    </section>
  )
}
